/**
 * Genre Mixer screen state.
 *
 * Feature state rather than a §4 product domain, held in a store for the same
 * reason as dnaStore: in the monolith a mix survived navigating away, because
 * every screen shared one component instance.
 *
 * The compatibility score, the DNA preview and the prompt are all *derived* from
 * these settings, so none of them are stored — see the selectors at the bottom.
 * The exceptions are `prompt`, which becomes independent state the moment the
 * user edits it (`promptDirty` records that), and `extraDescription`, which is
 * a separate submit-time addendum rather than part of the generated prompt.
 *
 * SIMULATED. Nothing here mixes audio: the compatibility score and the DNA figures are
 * hashes of the settings, and the song comes from generationApi's fabricated generator.
 */
import { create } from 'zustand';
import { GM_LENGTHS, GM_MOODS, GM_TEMPOS } from '../data/music';
import { TEXT_MUSIC_GENERATION_CREDITS } from '../lib/credits';
import { buildMixerPrompt, buildMusicDna, genreCompatibility } from '../lib/music';
import type { GenreCompatibility, MixerSettings } from '../types/domain';

/** Credits a genre mix costs. Priced by the prototype. */
export const MIXER_COST = TEXT_MUSIC_GENERATION_CREDITS;

/*
  MIXER_STEPS — the four progress rows "Analyzing Genres" … "Rendering Audio" — is gone,
  along with `progressStep`. The rows advanced on a 320ms interval that had nothing to do
  with the request, so they named stages that were not happening and reported elapsed
  time as work completed. The screen now shows the app-wide processing state instead.
*/

/** Validation messages, verbatim. */
export const MIXER_ERRORS = {
  noGenres: 'Select at least two genres to create your mix.',
  noPrompt: 'Generate a prompt before creating your song.',
  noExtraDescription: 'Add extra description before creating your song.',
} as const;

/**
 * How many genres a mix needs. Two, and the number is named because three things read
 * it — the instruction, the Mix button's disabled state, and `selectMixerReady`.
 */
export const MIXER_MIN_GENRES = 2;

/**
 * Where the user is in the mix.
 *
 * The screen used to have two states inferred from the settings: both genres chosen, or
 * not. That made "choose genres" and "fill in the details" the same moment — the whole
 * lower half of the page appeared the instant a second dropdown changed, with nothing
 * in between and nothing to press. The stages are now explicit, because the flow has a
 * step ("Mix") that no combination of settings can represent:
 *
 *   preparing — the workspace is being set up. Entry state, once per session.
 *   genres    — choosing. Mix is offered, and refused until two are chosen.
 *   mixing    — Mix was pressed. The details are being prepared.
 *   details   — the creative controls, the prompt and Create.
 *
 * `mixing` and `preparing` are UX processing states and nothing else: no genres are
 * blended in either, no request is made, and the store is unchanged when they end. See
 * lib/processing.ts, which is explicit about what that does and does not claim.
 */
export type MixerStage = 'preparing' | 'genres' | 'mixing' | 'details';

interface MixerState extends MixerSettings {
  /** Which step of the mix the screen is showing. See MixerStage. */
  stage: MixerStage;
  /** The editable prompt. */
  prompt: string;
  /** Extra text appended only when Create is submitted. */
  extraDescription: string;
  /** True once hand-edited, which stops the auto-generator overwriting it. */
  promptDirty: boolean;
  generating: boolean;
  /** Id of the generated song, resolved against the songs store by the route. */
  resultSongId: string | null;
  /** Validation message above the Generate button, or '' when clear. */
  error: string;

  /**
   * Updates one setting and clears `promptDirty`, so the prompt regenerates from
   * the new settings — the prototype's behaviour for every control.
   */
  setSetting: <K extends MixerSettingField>(field: K, value: MixerSettings[K]) => void;
  setPrompt: (prompt: string) => void;
  setExtraDescription: (extraDescription: string) => void;
  /** Picks an AI variation and rerolls the prompt. */
  selectVariation: (variation: string) => void;
  /** Rerolls the prompt from the current settings. */
  regeneratePrompt: () => void;
  /** Writes the auto-generated prompt, unless the user has edited it. */
  applyGeneratedPrompt: (prompt: string) => void;
  setError: (error: string) => void;
  /** Moves to a stage. The route drives this; nothing here advances on its own. */
  setStage: (stage: MixerStage) => void;
  setGenerating: (generating: boolean) => void;
  setResultSongId: (songId: string | null) => void;
  reset: () => void;
}

/** The settings fields `setSetting` accepts. */
export type MixerSettingField =
  'genre1' | 'genre2' | 'genre3' | 'mood' | 'energy' | 'tempo' | 'language' | 'vocal' | 'length';

const INITIAL = {
  // 'preparing' is the entry state: the screen opens into it and the route leaves it
  // after the processing floor. Because this store outlives the route (that is why it
  // is a store — see the header), coming back mid-session does NOT replay it: the stage
  // is only reset by reset(), and the route's entry effect runs once per mount and
  // checks the stage first.
  stage: 'preparing' as MixerStage,
  genre1: '',
  genre2: '',
  genre3: '',
  mood: GM_MOODS[0],
  energy: 70,
  tempo: GM_TEMPOS[1],
  language: 'English',
  vocal: 'Female',
  length: GM_LENGTHS[1],
  variation: null as string | null,
  variant: 0,
  prompt: '',
  extraDescription: '',
  promptDirty: false,
  generating: false,
  resultSongId: null,
  error: '',
};

export const useMixerStore = create<MixerState>((set) => ({
  ...INITIAL,

  setSetting(field, value) {
    set((s) => {
      const next = { [field]: value, promptDirty: false } as Partial<MixerState>;
      /*
        Changing a required genre after the details are open sends the user back to
        choosing, because the details were prepared FOR a particular blend — leaving them
        up would mean the prompt, the DNA preview and the compatibility score all
        describe a mix that is no longer selected, and Create would charge for it.

        `genre3` is deliberately not in this list: it is optional, it cannot take the
        selection below the minimum, and the details are still about the same blend with
        or without it.
      */
      const requiredGenre = field === 'genre1' || field === 'genre2';
      if (requiredGenre && s.stage === 'details') next.stage = 'genres';
      return next;
    });
  },

  setPrompt(prompt) {
    set({ prompt, promptDirty: true });
  },

  setExtraDescription(extraDescription) {
    set({ extraDescription });
  },

  selectVariation(variation) {
    set((s) => ({ variation, promptDirty: false, variant: s.variant + 1 }));
  },

  regeneratePrompt() {
    set((s) => ({ promptDirty: false, variant: s.variant + 1 }));
  },

  applyGeneratedPrompt(prompt) {
    set((s) => (s.promptDirty ? {} : { prompt }));
  },

  setError(error) {
    set({ error });
  },

  setStage(stage) {
    set({ stage });
  },

  setGenerating(generating) {
    set({ generating });
  },

  setResultSongId(resultSongId) {
    set({ resultSongId });
  },

  reset() {
    set({ ...INITIAL });
  },
}));

// ---------------------------------------------------------------------------
// Derived reads
//
// Plain functions over a settings snapshot rather than store fields: they are
// cheap, pure and always consistent with the settings. Storing them would mean
// keeping four values in sync on every keystroke, which is what made the
// monolith's mixer state so easy to desynchronise.
// ---------------------------------------------------------------------------

/**
 * True once the two required genres are chosen — which is what gates the Mix button.
 *
 * The first two slots specifically, not "any two of the three", and that is a real
 * constraint rather than a preference: genreCompatibility, buildMusicDna and
 * buildMixerPrompt all return null or '' unless genre1 AND genre2 are set (see
 * lib/music.ts). Counting a mix of genre1 + genre3 as ready would open the details stage
 * with no compatibility score, no DNA preview and an empty prompt — so the picker asks
 * for the two required slots first and offers the third as an addition, which is how it
 * is labelled.
 */
export const selectMixerReady = (s: MixerSettings): boolean => Boolean(s.genre1 && s.genre2);

/**
 * How many genres are chosen, for the instruction's "2 of 2 selected" readout.
 *
 * Counts the optional third, because it IS a chosen genre and a count that ignored it
 * would contradict the screen. The gate above is a different question.
 */
export const selectGenreCount = (s: MixerSettings): number =>
  [s.genre1, s.genre2, s.genre3].filter(Boolean).length;

/** Blend score for the compatibility ring. Null until both genres are chosen. */
export const selectCompatibility = (s: MixerSettings): GenreCompatibility | null =>
  genreCompatibility(s.genre1, s.genre2);

/** The DNA preview grid's values. Null until both genres are chosen. */
export const selectMixerDna = (s: MixerSettings) =>
  buildMusicDna({
    genre1: s.genre1,
    genre2: s.genre2,
    genre3: s.genre3,
    mood: s.mood,
    tempo: s.tempo,
    energy: s.energy,
    variation: s.variation,
  });

/** The auto-generated prompt for the current settings. */
export const selectGeneratedPrompt = (s: MixerSettings): string => buildMixerPrompt(s);
